/**
 * @fileoverview AR-924/AR-932 — State machine of the "Upload for class" modal: stage files/links, match
 * students, then send. Files go out in one request per set of recipients (the single-student route for one
 * student, `files/bulk` for a group, so a shared file is uploaded once) and links one request each. Progress
 * is tracked per (item, student), so a second confirm only retries what was not sent.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getTeacherDeliverablePort } from '../teacherUpload'
import type { MatchableStudent, UploadProgress } from '../teacherUpload'
import {
  batchFilesByRecipients, fullySent, groupByStudent, pending, releasePreviews, stageFile, stageLink, summarize,
  uploadable, type FileBatch, type StagedItem,
} from './stagedItems'

export type UploadStep = 'add_files' | 'match'
const PERCENT = 100

export interface UploadForClassResult {
  /** Students whose every pending item was sent. */
  done: number
  /** Students with at least one item the backend refused. */
  failed: number
  total: number
}

/** Per student: did every file they waited for arrive, and did at least one (so the delivery exists). */
interface FilesOutcome {
  all: boolean
  any: boolean
}

export function useUploadForClass(
  activityId: string, students: readonly MatchableStudent[], defaultDate: string | null,
) {
  const port = getTeacherDeliverablePort()
  const [step, setStep] = useState<UploadStep>('add_files')
  const [items, setItems] = useState<StagedItem[]>([])
  const [sameDate, setSameDate] = useState(true)
  const [date, setDate] = useState<string | null>(defaultDate)
  const [submitting, setSubmitting] = useState(false)

  const itemsRef = useRef(items)
  itemsRef.current = items
  useEffect(() => () => releasePreviews(itemsRef.current), [])

  const update = useCallback((id: string, change: (item: StagedItem) => Partial<StagedItem>) => {
    setItems(current => current.map(item => (item.id === id ? { ...item, ...change(item) } : item)))
  }, [])
  const patch = useCallback((id: string, changes: Partial<StagedItem>) => update(id, () => changes), [update])

  const addFiles = useCallback((files: File[]) => {
    setItems(current => [...current, ...files.map(file => stageFile(file, students))])
  }, [students])
  const addLink = useCallback((url: string, studentIds: readonly string[]) => {
    setItems(current => [...current, stageLink(url, studentIds, students)])
  }, [students])
  const remove = useCallback((id: string) => {
    releasePreviews(itemsRef.current.filter(item => item.id === id))
    setItems(current => current.filter(item => item.id !== id))
  }, [])
  const clear = useCallback(() => {
    releasePreviews(itemsRef.current)
    setItems([])
  }, [])
  const assign = useCallback((id: string, studentIds: readonly string[]) => {
    patch(id, { studentIds, matchedBy: studentIds.length > 0 ? 'user' : null })
  }, [patch])

  const summary = useMemo(() => summarize(items), [items])
  const unassigned = useMemo(() => uploadable(items).filter(item => item.studentIds.length === 0).length, [items])
  const studentsCount = useMemo(() => groupByStudent(items).size, [items])
  const canProceed = summary.ready > 0 && !submitting
  // Figma: the CTA stays enabled while a file still "needs a student" — that file simply stays staged.
  const canConfirm = step === 'match' && studentsCount > 0 && !submitting

  /** The item is done once every student has it; a later failure for another student marks it failed. */
  const markSent = useCallback((id: string, studentId: string) => {
    update(id, item => {
      const sent = { ...item, sentTo: [...item.sentTo, studentId] }
      return fullySent(sent) ? { sentTo: sent.sentTo, status: 'uploaded', percent: PERCENT } : { sentTo: sent.sentTo }
    })
  }, [update])

  /** Resolves with the students who got the files; a student the backend skipped or refused is not among them. */
  const requestBatch = useCallback(async (
    { studentIds, files }: FileBatch, onProgress: (progress: UploadProgress) => void,
  ) => {
    const fileList = files.map(item => item.file as File)
    if (studentIds.length === 1) {
      await port.uploadFiles({ activityId, studentId: studentIds[0], files: fileList, onProgress })
      return [...studentIds]
    }
    const result = await port.uploadFilesForStudents({ activityId, studentIds, files: fileList, onProgress })
    return studentIds.filter(studentId => result.uploadedStudentIds.includes(studentId))
  }, [port, activityId])

  const sendBatch = useCallback(async (batch: FileBatch): Promise<Map<string, boolean>> => {
    const { studentIds, files } = batch
    files.forEach(item => patch(item.id, { status: 'uploading', percent: 0, failReason: null }))
    const onProgress = ({ loaded, total }: UploadProgress) => {
      const percent = total ? Math.round((loaded / total) * PERCENT) : 0
      files.forEach(item => patch(item.id, { percent }))
    }
    let received: string[] = []
    try {
      received = await requestBatch(batch, onProgress)
    } catch {
      received = []
    }
    const outcome = new Map(studentIds.map(studentId => [studentId, received.includes(studentId)]))
    files.forEach(item => {
      received.forEach(studentId => markSent(item.id, studentId))
      if (received.length < studentIds.length) patch(item.id, { status: 'failed', failReason: 'upload', percent: 0 })
    })
    return outcome
  }, [requestBatch, patch, markSent])

  const sendAllFiles = useCallback(async (): Promise<Map<string, FilesOutcome>> => {
    const outcomes = new Map<string, FilesOutcome>()
    for (const batch of batchFilesByRecipients(items)) {
      const sent = await sendBatch(batch)
      sent.forEach((ok, studentId) => {
        const previous = outcomes.get(studentId) ?? { all: true, any: false }
        outcomes.set(studentId, { all: previous.all && ok, any: previous.any || ok })
      })
    }
    return outcomes
  }, [items, sendBatch])

  /** Uploads and links never carry the date: it is stored by one PATCH per student, once the delivery exists. */
  const sendDate = useCallback(async (studentId: string) => {
    if (!sameDate || !date) return true
    try {
      await port.updateWorkCompletedAt(activityId, studentId, date)
      return true
    } catch {
      return false
    }
  }, [port, activityId, sameDate, date])

  const sendLink = useCallback(async (studentId: string, link: StagedItem) => {
    try {
      await port.addLink(activityId, studentId, link.url as string)
      markSent(link.id, studentId)
      return true
    } catch {
      patch(link.id, { status: 'failed', failReason: 'upload' })
      return false
    }
  }, [port, activityId, patch, markSent])

  const confirm = useCallback(async (): Promise<UploadForClassResult> => {
    setSubmitting(true)
    const groups = groupByStudent(items)
    const filesOutcome = await sendAllFiles()
    let done = 0
    let failed = 0
    for (const [studentId, group] of groups) {
      const files = filesOutcome.get(studentId)
      let allSent = files?.all ?? true
      let anySent = files?.any ?? false
      for (const link of group.filter(item => item.kind === 'link')) {
        const linkSent = await sendLink(studentId, link)
        allSent = allSent && linkSent
        anySent = anySent || linkSent
      }
      // A date that could not be saved counts as a failure for the student; the teacher fixes it in the drawer.
      if (anySent) allSent = (await sendDate(studentId)) && allSent
      if (allSent) done += 1
      else failed += 1
    }
    setSubmitting(false)
    return { done, failed, total: groups.size }
  }, [items, sendAllFiles, sendLink, sendDate])

  const dirty = items.some(pending) || submitting

  return {
    step,
    setStep,
    items,
    addFiles,
    addLink,
    remove,
    clear,
    assign,
    sameDate,
    setSameDate,
    date,
    setDate,
    summary,
    unassigned,
    studentsCount,
    canProceed,
    canConfirm,
    submitting,
    confirm,
    dirty,
  }
}

export type UploadForClassState = ReturnType<typeof useUploadForClass>
